import { Skeleton } from "@/components/kit";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import type { ProfitCampaignSummaryRowDTO } from "../../domain/types";
import { ReportMobileCard, ReportStat } from "./ReportProductLink";

type ProfitCampaignSummaryTableProps = {
  rows?: ProfitCampaignSummaryRowDTO[];
  isLoading?: boolean;
};

function displayValue(value: string | null | undefined, fallback: string) {
  return value?.trim() ? value : fallback;
}

function rowKey(row: ProfitCampaignSummaryRowDTO) {
  return [
    row.platform,
    row.campaignName,
    row.mappedProductId,
    row.mappedCategoryId,
  ].join(":");
}

function CampaignMapping({ row }: { row: ProfitCampaignSummaryRowDTO }) {
  return (
    <div className="flex flex-col">
      <span>{displayValue(row.mappedProductName, "ບໍ່ຜູກສິນຄ້າ")}</span>
      <span className="text-muted-foreground text-xs">
        {displayValue(row.mappedCategoryName, "ບໍ່ຜູກຫມວດ")}
      </span>
    </div>
  );
}

function CampaignSummaryMobileCard({
  row,
}: {
  row: ProfitCampaignSummaryRowDTO;
}) {
  return (
    <ReportMobileCard>
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">
          {displayValue(row.campaignName, "ບໍ່ມີ campaign")}
        </span>
        <span className="text-muted-foreground text-xs">
          {displayValue(row.platform, "ບໍ່ມີແພລດຟອມ")}
        </span>
      </div>
      <div className="mt-2 text-sm">
        <CampaignMapping row={row} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat
          label="ຄ່າແອດ"
          value={formatLak(row.totalSpend)}
          valueClassName="font-display text-secondary text-lg"
        />
        <ReportStat label="ລາຍການ" value={row.expenseCount} />
        <ReportStat
          label="ຊ່ວງເວລາ"
          value={`${formatDateLocal(row.firstSpentAt)} - ${formatDateLocal(row.lastSpentAt)}`}
          valueClassName="text-muted-foreground text-xs font-normal"
          className="col-span-2"
        />
      </dl>
    </ReportMobileCard>
  );
}

export function ProfitCampaignSummaryTable({
  rows = [],
  isLoading,
}: ProfitCampaignSummaryTableProps) {
  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }

  return (
    <section className="brand-card overflow-hidden">
      <div className="flex flex-col gap-1 p-4 sm:p-5">
        <h2 className="font-display text-lg">ສະຫຼຸບ Campaign</h2>
        <p className="text-muted-foreground text-sm">
          ຄ່າແອດຕາມ platform / campaign ແລະ mapping ສິນຄ້າ
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="border-secondary/15 border-t p-8 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນ campaign ໃນຊ່ວງນີ້
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 border-secondary/15 border-t p-4 md:hidden">
            {rows.map((row) => (
              <CampaignSummaryMobileCard key={rowKey(row)} row={row} />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">Campaign</th>
                  <th className="px-4 py-3 font-medium">ການຜູກ</th>
                  <th className="px-4 py-3 text-right font-medium">ຄ່າແອດ</th>
                  <th className="px-4 py-3 text-right font-medium">ລາຍການ</th>
                  <th className="px-4 py-3 font-medium">ຊ່ວງເວລາ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    className="border-secondary/10 border-t"
                  >
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {displayValue(row.campaignName, "ບໍ່ມີ campaign")}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {displayValue(row.platform, "ບໍ່ມີແພລດຟອມ")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <CampaignMapping row={row} />
                    </td>
                    <td className="px-4 py-3 text-right font-display text-secondary text-xl tabular-nums">
                      {formatLak(row.totalSpend)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.expenseCount}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatDateLocal(row.firstSpentAt)} -{" "}
                      {formatDateLocal(row.lastSpentAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
